import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, CheckCircle2, ChevronRight, ChevronLeft } from 'lucide-react';
import Container from '../../components/layout/Container';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import Icon from '../../components/common/Icon';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { useStore } from '../../hooks/useStore';
import { useToast } from '../../contexts/ToastContext';
import { orderService } from '../../services/orderService';
import { addressService } from '../../services/addressService';
import { formatPrice } from '../../utils/priceCalculator';
import { fetchCepData, unmask } from '../../utils/maskHelpers';
import { formatAddress } from '../../utils/addressFormatter';

const STEPS = [
  { key: 'address', label: 'Endereço', icon: MapPin },
  { key: 'payment', label: 'Pagamento', icon: CreditCard },
  { key: 'review', label: 'Revisão', icon: CheckCircle2 },
];

// Barra de steps visual. Active = etapa atual; completed = etapas anteriores.
const Stepper = ({ currentIdx }) => (
  <ol className="flex items-center justify-between mb-8">
    {STEPS.map((step, idx) => {
      const completed = idx < currentIdx;
      const active = idx === currentIdx;
      return (
        <li key={step.key} className="flex-1 flex items-center">
          <div className="flex flex-col items-center flex-shrink-0">
            <span
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ring-4 ring-white transition-colors ${
                completed || active ? 'bg-primary text-white' : 'bg-app-surface-hover text-app-text-secondary'
              }`}
            >
              {completed ? <CheckCircle2 size={20} /> : <Icon as={step.icon} size="sm" />}
            </span>
            <span
              className={`text-xs mt-1.5 font-medium ${
                active ? 'text-primary' : completed ? 'text-app-text' : 'text-app-text-secondary'
              }`}
            >
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 ${completed ? 'bg-primary' : 'bg-app-border'}`} />
          )}
        </li>
      );
    })}
  </ol>
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { store } = useStore();
  const {
    items,
    coupon,
    shipping,
    getSubtotal,
    getTotalCommission,
    getCouponDiscount,
    getShippingCost,
    getTotalToPay,
    isEmpty,
    clearCart
  } = useCart();
  const { success, error, warning } = useToast();

  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [form, setForm] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    paymentMethod: 'boleto',
    notes: '',
  });

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    addressService
      .list()
      .then((rows) => {
        if (cancelled) return;
        setAddresses(rows || []);
        const def = rows?.find((r) => r.isDefault) || rows?.[0];
        if (def) setSelectedAddressId(String(def.id));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleField = (field) => (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCepBlur = useCallback(async () => {
    if (unmask(form.cep).length !== 8) return;
    setCepLoading(true);
    try {
      const data = await fetchCepData(form.cep);
      if (data) {
        setForm((prev) => ({
          ...prev,
          street: prev.street || data.logradouro,
          neighborhood: prev.neighborhood || data.bairro,
          city: prev.city || data.localidade,
          state: prev.state || data.uf,
        }));
      } else {
        warning('CEP não encontrado — preencha manualmente.');
      }
    } finally {
      setCepLoading(false);
    }
  }, [form.cep, warning]);

  const validateAddress = () => {
    if (selectedAddressId) return true;
    if (unmask(form.cep).length !== 8) return error('CEP inválido') && false;
    if (!form.street.trim()) return error('Rua é obrigatória') && false;
    if (!form.number.trim()) return error('Número é obrigatório') && false;
    if (!form.neighborhood.trim()) return error('Bairro é obrigatório') && false;
    if (!form.city.trim()) return error('Cidade é obrigatória') && false;
    if (form.state.length !== 2) return error('UF inválido') && false;
    return true;
  };

  const nextStep = () => {
    if (stepIdx === 0 && !validateAddress()) return;
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };

  const prevStep = () => setStepIdx((i) => Math.max(0, i - 1));

  const handleFinish = async () => {
    if (!store) {
      error('Loja não identificada.');
      return;
    }
    if (!items.length) {
      warning('Sacola vazia.');
      return;
    }
    setLoading(true);
    try {
      let shippingAddressId = selectedAddressId ? parseInt(selectedAddressId, 10) : null;
      if (!shippingAddressId) {
        const created = await addressService.create({
          label: 'Entrega',
          zip: unmask(form.cep),
          street: form.street,
          number: form.number || null,
          complement: form.complement || null,
          district: form.neighborhood || null,
          city: form.city,
          state: form.state,
          country: 'BR',
          isDefault: addresses.length === 0,
        });
        shippingAddressId = created.id;
      }
      const notes = [form.notes, `Método preferido: ${form.paymentMethod}`].filter(Boolean).join('\n');
      const order = await orderService.create({
        items: items.map((item) => ({ productVariationId: item.variationId, quantity: item.quantity })),
        shippingAddressId,
        shippingCost: getShippingCost(),
        notes,
        source: 'web_checkout',
        couponCode: coupon?.code || null,
      });
      clearCart();
      success(`Pedido ${order.formattedNumber || `#${order.id}`} criado!`);
      // Pós-checkout: tela de confirmação (Doc Seção 9.5).
      setTimeout(() => navigate(`/pedido/${order.id}/confirmacao`), 800);
    } catch (err) {
      error(err.message || 'Erro ao finalizar pedido.');
      setLoading(false);
    }
  };

  if (isEmpty) {
    return (
      <Container>
        <div className="text-center py-12">
          <p className="text-lg text-app-text-secondary mb-4">Sua sacola está vazia.</p>
          <Button onClick={() => navigate('/catalogo')}>Ver catálogo</Button>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <div className="text-center py-12">
          <p className="text-lg text-app-text-secondary mb-4">
            Você precisa estar logado pra finalizar a compra.
          </p>
          <Button onClick={() => navigate('/entrar')}>Entrar</Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <h1 className="text-3xl font-extrabold text-app-text mb-6">Finalizar pedido</h1>
      <Stepper currentIdx={stepIdx} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {stepIdx === 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-4">Endereço de entrega</h2>
              {addresses.length > 0 && (
                <div className="mb-4">
                  <Select
                    label="Usar endereço salvo"
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                    options={[
                      { value: '', label: 'Cadastrar novo endereço' },
                      ...addresses.map((a) => ({
                        value: String(a.id),
                        label: `${a.label || 'Endereço'} — ${a.street}, ${a.number || 's/n'} — ${a.city}/${a.state}${
                          a.isDefault ? ' (padrão)' : ''
                        }`,
                      })),
                    ]}
                    helpText={
                      <>
                        Gerencie em{' '}
                        <a href="/minha-conta/enderecos" className="text-primary hover:underline">
                          Minha Conta › Endereços
                        </a>
                      </>
                    }
                  />
                </div>
              )}
              {!selectedAddressId && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <MaskedInput
                      label="CEP"
                      mask="cep"
                      value={form.cep}
                      onChange={handleField('cep')}
                      onBlur={handleCepBlur}
                      placeholder="00000-000"
                      helpText={cepLoading ? 'Buscando…' : 'Autopreenche ao digitar'}
                      required
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Rua"
                        value={form.street}
                        onChange={handleField('street')}
                        placeholder="Nome da rua"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Número"
                      value={form.number}
                      onChange={handleField('number')}
                      placeholder="123"
                      required
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Complemento"
                        value={form.complement}
                        onChange={handleField('complement')}
                        placeholder="Apto, bloco, ref."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Bairro"
                      value={form.neighborhood}
                      onChange={handleField('neighborhood')}
                      required
                    />
                    <Input label="Cidade" value={form.city} onChange={handleField('city')} required />
                    <Input
                      label="UF"
                      value={form.state}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))
                      }
                      placeholder="SP"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>
              )}
            </Card>
          )}

          {stepIdx === 1 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-4">Método de pagamento</h2>
              <Select
                value={form.paymentMethod}
                onChange={handleField('paymentMethod')}
                options={[
                  { value: 'boleto', label: 'Boleto bancário' },
                  { value: 'pix', label: 'PIX' },
                  { value: 'credit_card', label: 'Cartão de crédito' },
                ]}
              />
              <div className="mt-4 p-4 bg-info/10 border border-info/30 rounded-lg text-sm text-info">
                Após a aprovação do fornecedor, você receberá o link de pagamento (PIX QR / boleto /
                tokenização de cartão) na área de pedidos. Nenhum dado sensível é coletado aqui.
              </div>

              <div className="mt-6">
                <Textarea
                  label="Observações (opcional)"
                  value={form.notes}
                  onChange={handleField('notes')}
                  placeholder="Alguma observação sobre o pedido…"
                  rows={3}
                />
              </div>
            </Card>
          )}

          {stepIdx === 2 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-4">Revisão</h2>
              <div className="space-y-4">
                <section>
                  <h3 className="text-sm font-semibold text-app-text-secondary uppercase tracking-wide mb-2">
                    Entrega
                  </h3>
                  {selectedAddressId ? (() => {
                    const saved = addresses.find(
                      (a) => String(a.id) === String(selectedAddressId)
                    );
                    return (
                      <p className="text-sm text-app-text">
                        {saved ? formatAddress(saved) : 'Endereço selecionado'}
                      </p>
                    );
                  })() : (
                    <p className="text-sm text-app-text">
                      {formatAddress({
                        street: form.street,
                        number: form.number,
                        complement: form.complement,
                        district: form.neighborhood,
                        city: form.city,
                        state: form.state,
                        zip: form.cep,
                      })}
                    </p>
                  )}
                </section>
                <section>
                  <h3 className="text-sm font-semibold text-app-text-secondary uppercase tracking-wide mb-2">
                    Pagamento
                  </h3>
                  <p className="text-sm text-app-text capitalize">
                    {form.paymentMethod === 'credit_card' ? 'Cartão de crédito' : form.paymentMethod}
                  </p>
                </section>
                {form.notes && (
                  <section>
                    <h3 className="text-sm font-semibold text-app-text-secondary uppercase tracking-wide mb-2">
                      Observações
                    </h3>
                    <p className="text-sm text-app-text whitespace-pre-line">{form.notes}</p>
                  </section>
                )}
              </div>
            </Card>
          )}

          <div className="flex justify-between mt-6">
            <Button
              variant="subtle"
              onClick={prevStep}
              disabled={stepIdx === 0}
              leftIcon={<ChevronLeft size={18} />}
            >
              Voltar
            </Button>
            {stepIdx < STEPS.length - 1 ? (
              <Button onClick={nextStep} rightIcon={<ChevronRight size={18} />}>
                Avançar
              </Button>
            ) : (
              <Button onClick={handleFinish} loading={loading} size="lg">
                Finalizar pedido
              </Button>
            )}
          </div>
        </div>

        {/* Resumo sticky */}
        <div>
          <Card className="p-6 lg:sticky lg:top-20">
            <h2 className="text-lg font-bold text-app-text mb-4">Resumo</h2>
            <ul className="space-y-3 mb-4">
              {items.map((item) => (
                <li key={item.id} className="flex gap-3">
                  <div className="w-14 h-14 rounded-md overflow-hidden bg-app-surface-hover flex-shrink-0">
                    <ImageWithFallback
                      src={item.image}
                      alt={item.productName}
                      fallbackText={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-text truncate">{item.productName}</p>
                    <p className="text-xs text-app-text-secondary">
                      {item.quantity} × {formatPrice(item.price)}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-app-text">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </li>
              ))}
            </ul>
            <dl className="space-y-2 text-sm border-t border-app-border pt-4">
              <div className="flex justify-between">
                <dt className="text-app-text-secondary">Subtotal:</dt>
                <dd>{formatPrice(getSubtotal())}</dd>
              </div>
              {getTotalCommission() > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Comissão ganha:</dt>
                  <dd className="font-semibold">+{formatPrice(getTotalCommission())}</dd>
                </div>
              )}
              {getCouponDiscount() > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Cupom {coupon?.code}:</dt>
                  <dd className="font-semibold">-{formatPrice(getCouponDiscount())}</dd>
                </div>
              )}
              {shipping && (
                <div className="flex justify-between">
                  <dt className="text-app-text-secondary">Frete ({shipping.name}):</dt>
                  <dd>
                    {getShippingCost() > 0
                      ? formatPrice(getShippingCost())
                      : <span className="text-success">Grátis</span>}
                  </dd>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-app-border">
                <dt>Total a pagar:</dt>
                <dd className="text-primary">{formatPrice(getTotalToPay())}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default CheckoutPage;
